import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useQuery } from '@/utils/api';
import { sToMs } from '@/utils/date.util';

export type AgentStatus = 'online' | 'offline';

export interface Agent {
  key: string; // Unique identifier for Table row selection (equals client_id)
  client_id: string;
  last_seen_at: number;
  hostname: string;
  fqdn: string;
  network_interfaces?: {
    name: string;
    ip_addresses: string[];
  }[];
  _idx: string;
  os?: string;
  os_version?: string;
  architecture?: string;
  health_check?: {
    disk_space?: number;
    memory?: number;
    virtualization_supported?: boolean;
    cloudflare?: boolean;
    cloudflared_status?: {
      status: string;
      connected: string;
      connection_count: number;
      tunnel_name: string;
      hostname: string;
      authorized_users: string;
    };
  };
}

export interface Filter {
  search?: string;
  status?: AgentStatus[];
  sort?: {
    orderBy: keyof Agent;
    descending: boolean;
  };
}

export function useGetAgents({
  enabled = true,
  filter,
}: {
  enabled?: boolean;
  filter?: Filter;
} = {}) {
  const axios = useAxios();

  // Fast initial load with basic agent data
  // Enhanced data is prefetched in background by useAgentBackgroundPrefetch
  const basicQuery = useQuery<Agent[]>({
    defaultErrorMessage: 'Unable to connect to the agent service.',
    getErrorFromResponse: true,
    queryKey: getQueryKey.getAgents('basic'),
    queryFn: async () => {
      const response = await axios.get('/agent/list');
      return response.data;
    },
    refetchInterval: sToMs(50), // Auto-refetch for live status
    enabled,
    retry: 1, // Allow 1 retry and shows error instead of default 3 retries
    staleTime: sToMs(45), // Reduce unnecessary refetches
    // Keep previous data visible during search to prevent blanking on every keystroke
    // Search is excluded from filterKey, so table doesn't remount
    // Filter/sort changes still remount via filterKey for scroll reset
    placeholderData: keepPreviousData,
  });

  // Use basic data directly - table doesn't need enhanced data
  // Enhanced data is loaded in background and populates drawer caches
  const data = (basicQuery.data ?? [])
    .filter(agent => agent.client_id !== 'server')
    .map(agent => ({
      ...agent,
      key: agent.client_id, // Add key for Table row selection/identification
    }));

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const filteredData = useMemo(() => {
    const searchLowerCased = filter?.search?.toLowerCase();

    const searchedData = searchLowerCased
      ? data.filter(agent => {
          if (filter?.search) {
            const ips =
              agent.network_interfaces?.flatMap(a => a.ip_addresses) ?? [];
            return (
              agent.client_id.toLowerCase().includes(searchLowerCased) ||
              agent.fqdn.toLowerCase().includes(searchLowerCased) ||
              agent.hostname.toLowerCase().includes(searchLowerCased) ||
              ips.some(ip => ip.includes(searchLowerCased))
            );
          }
          return true;
        })
      : data;

    const statusFiltered =
      filter?.status && filter.status.length > 0 && filter.status.length < 2
        ? filterByStatus(searchedData, filter.status)
        : searchedData;

    const dataSorted = filter?.sort
      ? statusFiltered.sort((a, b) => {
          const field =
            filter.sort?.orderBy.toString() === 'status'
              ? ('last_seen_at' as keyof Agent)
              : filter.sort?.orderBy;
          if (!field) return 0;
          const result = filter?.sort?.descending
            ? a[field] && b[field] && a[field] > b[field]
            : a[field] && b[field] && a[field] < b[field];
          return result ? 1 : -1;
        })
      : statusFiltered;

    return dataSorted;
  }, [data, filter]);

  function filterByStatus(agents: Agent[], status: AgentStatus[]) {
    const now = new Date();

    return agents.filter(agent => {
      if (!agent.last_seen_at) return false;
      const isoString = new Date(
        Math.floor(Number(agent.last_seen_at) / 1000)
      ).toISOString();

      const visitedDate = new Date(isoString);
      const diffInMinutes =
        (now.getTime() - visitedDate.getTime()) / (1000 * 60);

      return status.find(s => {
        if (s === 'online') return diffInMinutes < 5;
        if (s === 'offline') return diffInMinutes >= 5;

        return false;
      });
    });
  }

  const aegisNotProvisioned =
    basicQuery.status === 'error' &&
    ((typeof basicQuery.error?.name === 'string' &&
      basicQuery.error.name.includes('aegis configuration not found')) ||
      (typeof basicQuery.error?.message === 'string' &&
        basicQuery.error.message.includes('aegis configuration not found')));

  return {
    data: filteredData,
    error: basicQuery.error,
    status: basicQuery.status,
    isLoadingBasic: basicQuery.isPending,
    // Enhanced data loading state - kept for backward compatibility
    // Enhanced data is now loaded via background prefetch, not in this hook
    isLoadingEnhanced: false,
    isFullyLoaded: basicQuery.isSuccess,
    hasEnhancedData: false, // Deprecated - enhanced data is in individual caches now
    fetchNextPage: () => {},
    isFetchingNextPage: false,
    isFetching: basicQuery.isFetching,
    hasNextPage: false,
    refetch: () => {
      basicQuery.refetch();
    },
    invalidate: () => {
      basicQuery.invalidate();
    },
    isAllEmpty: basicQuery.status === 'success' && data.length === 0,
    isFilteredEmpty:
      basicQuery.status !== 'pending' && filteredData.length === 0,
    aegisNotProvisioned,
  };
}
